import { EPixelEventName } from '@/constants/pixel';
import useDiseaseEncyclopediaByUser from '@/entities/disease-encyclopedia/hooks/useDiseaseEncyclopediaByUser';
import DiseaseEncyclopediaModel, {
  DiseaseEncyclopediaBannerModel,
  DiseaseEncyclopediaFaqModel,
  EDiseaseEncyclopediaBottomButtonType,
} from '@/entities/disease-encyclopedia/model/diseaseEncyclopedia';
import useNavigate from '@/hooks/useNavigate';
import { Client } from '@/types/app';
import { ROUTES } from '@/types/common';
import { isWebView } from '@/utils/mobile/RNPlugins';
import Pixel from '@/utils/pixel/pixel';
import { useRouter } from 'next/router';
import React from 'react';
import { useEffect } from 'react';

export interface DiseaseEncyclopediaDetailViewModel {
  diseaseEncyclopedia?: DiseaseEncyclopediaModel;
  shouldShowDiseaseImage: () => boolean;
  shouldShowDepartmentTags: () => boolean;
  shouldShowSearchTags: () => boolean;
  shouldShowSymptomTags: () => boolean;
  shouldShowBanner: () => boolean;
  shouldShowCtaBottomFloatingButton: () => boolean;
  shouldShowBottomFloatingSocialProof: () => boolean;
  getCtaBottomFloatingButtonText: () => string;
  shouldShowTableOfContents: () => boolean;
  shouldShowContent: () => boolean;
  showShowFaqs: () => boolean;
  handleClickCtaBottomFloatingButton: () => void;
  getContent: () => string;
  getBanner: () => DiseaseEncyclopediaBannerModel | undefined;
  getTitle: () => string;
  getFaqTitle: () => string;
  getFaqs: () => Array<DiseaseEncyclopediaFaqModel>;
  onClickTopCta: () => void;
  onClickOuterLink: (url: string) => void;
  extractTextFromChildren: (children: React.ReactNode) => string;
}

interface Props {
  client: Client;
  publicId?: string;
}

const useDiseaseEncyclopediaDetailViewModel = ({ client, publicId }: Props): DiseaseEncyclopediaDetailViewModel => {
  const router = useRouter();
  const navigate = useNavigate();
  const id = publicId || (router.query.id as string);
  const { diseaseEncyclopedia } = useDiseaseEncyclopediaByUser(id);

  const shouldShowDiseaseImage = (): boolean => {
    return !!diseaseEncyclopedia?.getTitleImageUrl();
  };

  const shouldShowDepartmentTags = (): boolean => {
    return (diseaseEncyclopedia?.getDepartmentTags()?.length ?? 0) > 0;
  };

  const shouldShowSearchTags = (): boolean => {
    return (diseaseEncyclopedia?.getSearchTags()?.length ?? 0) > 0;
  };

  const shouldShowSymptomTags = (): boolean => {
    return (diseaseEncyclopedia?.getSymptomTags()?.length ?? 0) > 0;
  };

  const shouldShowTableOfContents = (): boolean => {
    return !!diseaseEncyclopedia?.getContent();
  };

  const shouldShowContent = (): boolean => {
    return !!diseaseEncyclopedia?.getContent();
  };

  const shouldShowBanner = (): boolean => {
    return !!diseaseEncyclopedia?.getBanner();
  };

  const shouldShowCtaBottomFloatingButton = (): boolean => {
    if (client.isWebView) {
      return false;
    }

    if (diseaseEncyclopedia?.getBottomButtonType() === EDiseaseEncyclopediaBottomButtonType.None) {
      return false;
    }

    return true;
  };

  const shouldShowFaqs = (): boolean => {
    return (diseaseEncyclopedia?.getFaqs()?.length ?? 0) > 0;
  };

  const handleClickCtaBottomFloatingButton = () => {
    Pixel.trackCustom(EPixelEventName.BottomCtaClick, {
      ...(diseaseEncyclopedia?.getTitle() && {
        disease: diseaseEncyclopedia?.getTitle(),
      }),
    });

    router.push(`${ROUTES.TELEMEDICINE.CATEGORIES.path}`);
  };

  const shouldShowBottomFloatingSocialProof = (): boolean => {
    return diseaseEncyclopedia?.getBottomButtonType() === EDiseaseEncyclopediaBottomButtonType.DiagnosisReservation;
  };

  const getCtaBottomFloatingButtonText = (): string => {
    if (diseaseEncyclopedia?.getBottomButtonType() === EDiseaseEncyclopediaBottomButtonType.DiagnosisReservation) {
      return '비대면 진료 받으러 가기';
    }
    return '실시간 의료상담 물어보기';
  };

  const getTitle = (): string => {
    return diseaseEncyclopedia?.getTitle() || '';
  };

  const getFaqTitle = (): string => {
    const title = diseaseEncyclopedia?.getTitle();
    return title && shouldShowFaqs() ? title + '의 자주 묻는 질문' : '';
  };

  const getContent = (): string => {
    return diseaseEncyclopedia?.getContent() || '';
  };

  const getFaqs = (): Array<DiseaseEncyclopediaFaqModel> => {
    return diseaseEncyclopedia?.getFaqs() || [];
  };

  const getBanner = (): DiseaseEncyclopediaBannerModel | undefined => {
    return diseaseEncyclopedia?.getBanner();
  };

  const onClickTopCta = () => {
    Pixel.trackCustom(EPixelEventName.TopCtaClick, {
      ...(diseaseEncyclopedia?.getTitle() && {
        disease: diseaseEncyclopedia?.getTitle(),
      }),
    });
  };

  const onClickOuterLink = (url: string) => {
    if (isWebView()) {
      navigate.navigateToPureWebViewAppScreen({
        uri: url,
      });
    }
  };

  const extractTextFromChildren = (children: React.ReactNode): string => {
    if (typeof children === 'string') {
      return children;
    }
    if (typeof children === 'number') {
      return String(children);
    }
    if (Array.isArray(children)) {
      return children.map(extractTextFromChildren).join('');
    }
    if (React.isValidElement(children) && children.props && children.props.children) {
      return extractTextFromChildren(children.props.children);
    }
    return '';
  };

  useEffect(() => {
    if (diseaseEncyclopedia) {
      Pixel.trackCustom(EPixelEventName.PageIn, {
        ...(diseaseEncyclopedia?.getTitle() && {
          disease: diseaseEncyclopedia?.getTitle(),
        }),
      });
    }
  }, [diseaseEncyclopedia]);

  return {
    diseaseEncyclopedia,
    shouldShowDiseaseImage,
    shouldShowDepartmentTags,
    shouldShowSearchTags,
    shouldShowSymptomTags,
    shouldShowBanner,
    shouldShowTableOfContents,
    shouldShowCtaBottomFloatingButton,
    shouldShowBottomFloatingSocialProof,
    getCtaBottomFloatingButtonText,
    shouldShowContent,
    showShowFaqs: shouldShowFaqs,
    handleClickCtaBottomFloatingButton,
    getContent,
    getBanner,
    getTitle,
    getFaqTitle,
    getFaqs,
    onClickTopCta,
    onClickOuterLink,
    extractTextFromChildren,
  };
};

export default useDiseaseEncyclopediaDetailViewModel;
